import {
    type TGetOrigin,
    type TSubscribeActionDescriptor,
    type TTeamsSendActionsDescriptor,
    Bridge,
    TeamsSendAction,
    TeamsSubscribeAction,
} from '@instant-messengers/vk-teams-bridge';
import { octoLogger } from '@mail-core/logger';
import { AuthClient } from '@vk-workspace/superapp-sdk/auth';
import type { SuperappBridgeType } from '@vk-workspace/superapp-sdk/bridge';
import { logger } from 'lib/logger';
import { parse } from 'qs';
import { APIConfig } from 'reactApp/api/APIConfig';
import { ACCESS_TOKEN_PARAMS } from 'reactApp/appHelpers/configHelpers';
import {
    accessTokenV2,
    WEBVIEW_OAUTH_TOKEN_RESOURCE,
    WEBVIEW_OAUTH_TOKEN_URL,
} from 'reactApp/appHelpers/featuresHelpers/features/accessTokenV2';
import { enableWorkspaceSuperappMode } from 'reactApp/appHelpers/featuresHelpers/features/workspaceSuperapp';
import { applyTheme } from 'reactApp/hooks/useDarkTheme';
import { storeCurrentTheme } from 'reactApp/modules/theme/theme.module';
import { TokenService } from 'reactApp/services/TokenService';
import { captureException } from 'reactApp/utils/tracer';

export type BridgeType = Bridge<
    TeamsSubscribeAction,
    Record<TeamsSubscribeAction, TSubscribeActionDescriptor>,
    TeamsSendAction,
    TTeamsSendActionsDescriptor
>;

type WorkspaceColorScheme = { colorsScheme?: 'light' | 'dark'; colorScheme?: 'light' | 'dark' } | null;

type InitReturnType = {
    isError: boolean;
    isEnabled: boolean;
};

const workspaceSuperappAccessToken = 'workspace-superapp-access-token';

export class WorkspaceSuperApp {
    isEnabled = false;
    isB2CMode = false;
    isAuthRequired = false;
    superAppOrigin: TGetOrigin['output'] = { origin: '', href: '' };
    private bridge: BridgeType;
    private tokenService: TokenService;
    private themeUnsubscribe: (() => void) | null = null;
    private readonly authClient: AuthClient;
    private accessTokenV2: string | null = null;

    constructor() {
        this.bridge = new Bridge();
        this.tokenService = new TokenService(this.bridge);
        this.authClient = new AuthClient(
            this.bridge as unknown as SuperappBridgeType,
            WEBVIEW_OAUTH_TOKEN_RESOURCE,
            WEBVIEW_OAUTH_TOKEN_URL,
            octoLogger
        );
    }

    async init(): Promise<InitReturnType> {
        this.isB2CMode = this.getIsB2CSuperApp();
        this.isEnabled = (this.checkIsSuperApp() || this.getIsSuperApp() || this.getIsB2CSuperApp()) && enableWorkspaceSuperappMode;
        this.isAuthRequired = this.isEnabled && !this.isB2CMode;

        if (!this.isEnabled) {
            return { isError: false, isEnabled: this.isEnabled };
        }

        try {
            this.themeUnsubscribe?.();
            this.themeUnsubscribe = null;

            const { store } = await import('reactApp/store');
            const { unsubscribe } = await this.bridge.subscribe(TeamsSubscribeAction.ThemeSettings, (data: WorkspaceColorScheme) => {
                const newTheme = data?.colorsScheme || data?.colorScheme;
                applyTheme(newTheme === 'dark');
                store.dispatch(storeCurrentTheme(newTheme || ''));
                this.sendThemeChangeEvent(newTheme);
            });
            this.themeUnsubscribe = unsubscribe;

            const themeSettings = await this.bridge.send(TeamsSendAction.GetThemeSettings, null);
            const initialTheme = themeSettings?.colorsScheme;
            applyTheme(initialTheme === 'dark');
            store.dispatch(storeCurrentTheme(initialTheme || ''));
        } catch (error) {
            captureException(error, { issueKey: 'workspace-superapp-theme-error' });
        }

        if (accessTokenV2 && this.isAccessTokenV2Enabled()) {
            try {
                const tokenResponse = await this.authClient.getAccessTokenV2();

                if (tokenResponse.ok) {
                    if (this.isAuthRequired) {
                        this.accessTokenV2 = tokenResponse.data.access_token;

                        if (this.accessTokenV2) {
                            APIConfig.set(workspaceSuperappAccessToken, this.accessTokenV2);
                        }
                    }

                    this.superAppOrigin = await this.bridge.send(TeamsSendAction.GetOrigin, null);

                    await this.bridge.send(TeamsSendAction.LoadingCompleted, {
                        ok: true,
                    });

                    return { isError: this.isAuthRequired ? !this.accessTokenV2 : false, isEnabled: this.isEnabled };
                }
            } catch (error) {
                if (error instanceof Error) {
                    logger.error('Error in getAuthToken', error.message);
                }

                throw error;
            }
        }

        let token;

        if (this.isAuthRequired) {
            token = await this.tokenService.init();

            if (token) {
                APIConfig.set(workspaceSuperappAccessToken, token);
            }
        }

        this.superAppOrigin = await this.bridge.send(TeamsSendAction.GetOrigin, null);

        await this.bridge.send(TeamsSendAction.LoadingCompleted, {
            ok: true,
        });

        return { isError: this.isAuthRequired ? !token : false, isEnabled: this.isEnabled };
    }

    private checkIsSuperApp() {
        const url = new URL(window.location.href);
        return url.searchParams.has('superapp');
    }

    private getIsSuperApp() {
        // по договоренности https://webim.internal.myteam.mail.ru/message/v1/QW9Lb3NBV0t3M1ItajlzX2ZRLzc1Nzc3MjA2MzEyMzY0OTQ0MDY=
        const url = new URL(window.location.href);
        return url.searchParams.get('source') === 'ws-superapp';
    }

    private getIsB2CSuperApp() {
        const url = new URL(window.location.href);

        return url.searchParams.get('source') === 'mail-superapp';
    }

    private isAccessTokenV2Enabled(): boolean {
        return (
            ACCESS_TOKEN_PARAMS.wssaAuthFlow === 'v2' &&
            (ACCESS_TOKEN_PARAMS.wssaAuthType === 'cookie' || ACCESS_TOKEN_PARAMS.wssaAuthType === 'token')
        );
    }

    getAuthToken() {
        return this.accessTokenV2 ?? this.tokenService.getAuthToken();
    }

    getAuthHeaderValue() {
        return `Bearer ${this.getAuthToken()}`;
    }

    sendAuthChangeEvent(path: string, params: string) {
        if (!this.isEnabled) {
            return;
        }

        this.bridge.send(TeamsSendAction.NavigationChanged, {
            path,
            params: parse(params, { ignoreQueryPrefix: true }),
        });
    }

    sendThemeChangeEvent(newTheme: string | undefined) {
        if (!this.isEnabled) {
            return;
        }

        const url = new URL(window.location.href);
        const parsedParams = {
            ...parse(url.search, { ignoreQueryPrefix: true }),
            ...(newTheme ? { theme: newTheme } : {}),
        };

        this.bridge.send(TeamsSendAction.NavigationChanged, {
            path: url.pathname,
            params: parsedParams,
        });
    }

    async fetchAccessToken() {
        try {
            if (accessTokenV2 && this.isAccessTokenV2Enabled()) {
                const refreshToken = await this.authClient.refreshAccessTokenV2();
                if (refreshToken.ok) {
                    this.accessTokenV2 = refreshToken.data.access_token;
                    APIConfig.set(workspaceSuperappAccessToken, this.accessTokenV2);

                    return this.accessTokenV2;
                }

                this.accessTokenV2 = null;
            }

            const token = await this.tokenService.fetchAccessToken();

            if (token) {
                APIConfig.set(workspaceSuperappAccessToken, token);
            }

            return token;
        } catch (error) {
            if (error instanceof Error) {
                logger.error('Error in getAuthToken', error.message);
            }

            throw error;
        }
    }

    refreshToken() {
        return this.fetchAccessToken();
    }

    openLink(url: string) {
        return this.bridge.send(TeamsSendAction.OpenLink, { url });
    }
}

let instance: WorkspaceSuperApp | null = null;

export const getWorkspaceSuperAppService = () => {
    if (typeof window === 'undefined') {
        return;
    }

    if (!instance) {
        instance = new WorkspaceSuperApp();
    }
    return instance;
};
